import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import {
  DataType,
  Form,
  FormDialogMode,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, Card, Grid, makeStyles, useTheme } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumToSelectOptionArray } from '../../../helpers'
import { useConfirmationDialog, useErrorDetailListDialog, useHttpError } from '../../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import { getCustomerDomain, storeCoefficients } from '../../../redux/helpers'

import {
  PriceCoefficientAPI,
  PriceCoefficientTable
} from '../../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import { EnumPriceComponentReference, EnumRedondeo } from '../../../api/Argentia/enums'
import { EnumCommodity, EnumPrecioUnitario, EnumTipoPeaje } from '../../../api/oData/enums'

import { ContractModel } from '../../../api/Argentia/model/contracts'

import {
  CreatePriceComponentRequest,
  PriceComponentTypeModel
} from '../../../api/Argentia/endpoints/priceComponents'
import {
  PricePeriodValueGroupedModel,
  PriceValuesModel
} from '../../../api/Argentia/endpoints/pricing/model/values'
import { EnumPresionTension } from '../../../api/oData/enums/enumPresionTension'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'
import { EnumRegionImpuestos } from '../../../api/oData/enums/enumRegionImpuestos'
import { ErrorDetailList } from '../../../components/errorDetailList/errorDetailList'
import useFlexibilities from '../../../components/shared/flexibilitiesTable/flexibilitiesTable.hooks'
import { PriceCoefficientsCard } from '../../../components/shared/priceCoefficientsCard/priceCoefficientsCard'
import usePriceCoefficients from '../../../components/shared/priceCoefficientsTable/priceCoefficientsTable.hooks'
import { updatePriceCoefficients } from '../../../components/shared/priceCoefficientsTable/priceCoefficientsTable.service'
import {
  getPriceComponent,
  getPriceComponentDefaults,
  getPriceComponentTypeById,
  getPriceComponentTypes,
  updatePriceComponentValue
} from '../../../components/shared/priceComponentsTable/priceComponentsTable.service'
import { PricePeriodCard } from '../../../components/shared/pricePeriodCard/pricePeriodCard'
import { PricePeriodFormDialog } from '../../../components/shared/pricePeriodFormDialog/pricePeriodFormDialog'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import {
  selectActiveFlexibility,
  selectClientContractByID
} from '../../../redux/selectors/client.selectors'
import { selectCoefficients } from '../../../redux/selectors/pricing.selectors'
import { fetchPressureGroup, fetchTolls } from '../../contracts/contracts.service'
import { getLeads } from '../../leads/leads.service'
import { useGetPricePeriodByID } from '../../pricing/priceComponentType/priceComponentTypeInstances/hooks/priceComponentType.instances.hooks'
import {
  bulkDeletePricePeriodValues,
  fetchPriceCurrentValues,
  updatePricePeriodValues
} from '../../pricing/pricing.service'
import {
  flexibilityToSelectOptions,
  mapPricePeriodFormDataToRequest,
  newPriceCoefficientsRequest,
  newPriceComponentRequestV2,
  priceCoefficientAPIListtoTableMapper,
  priceCoefficientsToAPIMapper
} from '../priceComponents.mapper'
import { createPriceComponentV2 } from '../priceComponents.service'
import { styles } from './priceComponentsAdd.styles'

export interface PriceComponentsAddPageProps {
  location: {
    state: {
      id?: number
      contractId?: number
      flexibilityId?: number
      priceType?: EnumPriceType
      componentTypesToExclude?: number[]

      // Este commodity se utiliza para crear el price
      // Si se navega desde pricing es necesario enviar el commodity!
      // Si no se envía commodity en la navegación se utiliza el commodity del contrato.
      // En el caso de que la navegación sea desde Grupos de Liquidación, puede no haber contrato, por lo que la commodity se obtiene del combo Componente de Precios elegido
      commodity?: number
      formMode?: FormDialogMode

      // PriceComponentTypeInstancesPage Navigation properties
      priceComponentTypeReference?: EnumPriceComponentReference // this property is needed when navigating from priceComponentTypeInstancesPage to handle form optional inputs
      tollID?: number // Needed when edit Form. this property is needed when navigating from priceComponentTypeInstancesPage to handle form toll selectOption readOnly/edit mode
      presionTensionID?: number // Needed when edit Form. this property is needed when navigating from priceComponentTypeInstancesPage to handle form presionTension selectOption readOnly/edit mode
      leadID?: number // Needed when edit Form. this property is needed when navigating from priceComponentTypeInstancesPage to handle form lead selectOption readOnly/edit mode
      regionImpuestosID?: number // Needed when edit Form. this property is needed when navigating from priceComponentTypeInstancesPage to handle form regionImpuestos selectOption readOnly/edit mode
      instancesToFilter?: number[] // this property is needed when navigating from priceComponentTypeInstancesPage to filter the lead/presionTension/peaje selectOptions
      parametroPeriodoPriceID?: number | null // this property is needed when priceValues of a ParameterPeriod price component type is being edited (navigation from priceComponentTypeInstancesPage)
      addInstanceFromPriceComponentType?: boolean // this flag REPLACE default behaviour of the page -> get priceComponent -> PriceId -> get Price && get priceValues  WITH get Price && get priceValues from idDummyPrice
      idDummyPrice?: number //  this property is used to fetch price unitPrice, rounding && Values when priceComponentID is not available
      priceComponentTypeID?: number //  this property is used to save new instance of a priceComponentType when priceComponentID is not available
    }
  }
}

export interface PriceComponentForm {
  id?: number
  contractID?: number
  contractCode: string
  flexibilityId?: number
  flexibilityCode?: string
  startDate?: string
  commodity: number
  priceComponentID: number
  priceComponentTypeID: number
  priceComponentTypeCode: string
  priceComponentTypeDescription: string
  priceCode: string
  priceType: EnumPriceType
  priceUnit: EnumPrecioUnitario
  round: EnumRedondeo
  defaultValue: number | null
  value: number | null
  toll?: EnumTipoPeaje | null // this property is needed when navigating from priceComponentTypeInstancesPage in order to create a new price instance of this price component type
  presionTension?: EnumPresionTension | null // this property is needed when navigating from priceComponentTypeInstancesPage in order to create a new price instance of this price component type
  lead?: number | null // this property is needed when navigating from priceComponentTypeInstancesPage in order to create a new price instance of this price component type
  leadCode?: string | null // this property is needed when navigating from priceComponentTypeInstancesPage in order to create a new price instance of this price component type
  regionImpuestos?: EnumRegionImpuestos | null // this property is needed when navigating from priceComponentTypeInstancesPage in order to create a new price instance of this price component type
  parametroPeriodoPriceID?: number | null // this property is needed when priceValues of a ParameterPeriod price component type is being edited (navigation from priceComponentTypeInstancesPage)
  idDummyPrice?: number //  this property is used to fetch price unitPrice, rounding && Values when priceComponentID is not available
}

export namespace ComponentAddButtonOptions {
  export const MANUAL = 'MANUAL'
  export const ASSISTED = 'ASSISTED'
}

export type ComponentAddButtonOptions =
  (typeof ComponentAddButtonOptions)[keyof typeof ComponentAddButtonOptions]

export const PriceComponentsAddPage: React.FC<PriceComponentsAddPageProps> = props => {
  const history = useHistory()
  const [t] = useTranslation([
    'priceComponent',
    'globalerisLibrary',
    'priceCoefficient',
    'enums',
    'sharedTranslations'
  ])
  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()
  const { getInformation, closeInformation, getConfirmation, closeConfirmation } =
    useConfirmationDialog()
  const { info, error, awaited } = useNotify()
  const { errorProps } = useHttpError()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const coefficients = useSelector(selectCoefficients)

  const contractID = props?.location?.state?.contractId
  const contract = useSelector(state => {
    return selectClientContractByID(state, contractID)
  }) as ContractModel
  const contractCode = contract?.contractCode

  const id = props?.location?.state?.id
  const flexibilityID = props?.location?.state?.flexibilityId
  const componentTypeToExclude = props?.location?.state?.componentTypesToExclude
  const commodity = props?.location?.state?.commodity ?? contract?.commodity
  const priceComponentTypeReference = props?.location?.state?.priceComponentTypeReference
  const priceTypeIsParametroPeriodo =
    props?.location?.state?.priceType === EnumPriceType.ParametroPeriodo
  const priceTypeIsParametro = props?.location?.state?.priceType === EnumPriceType.Parametro
  const priceTypeIsApuntamiento = props?.location?.state?.priceType === EnumPriceType.Apuntamiento
  const tollID = props?.location?.state?.tollID
  const presionTensionID = props?.location?.state?.presionTensionID
  const leadID = props?.location?.state?.leadID
  const regionImpuestosID = props?.location?.state?.regionImpuestosID
  const instancesToFilter = props?.location?.state?.instancesToFilter ?? []
  const parametroPeriodoPriceID = props?.location?.state?.parametroPeriodoPriceID
  const editFormMode =
    (!!id && !props?.location?.state?.formMode) ||
    props?.location?.state?.formMode === FormDialogMode.EDIT
  const addFormMode = props?.location?.state?.formMode === FormDialogMode.ADD
  const addInstance = props?.location?.state?.addInstanceFromPriceComponentType ?? false
  const idDummyPrice = props?.location?.state?.idDummyPrice
  const priceComponentTypeID = props?.location?.state?.priceComponentTypeID
  const referenceType = priceComponentTypeReference
    ? priceComponentTypeReference
    : flexibilityID
      ? EnumPriceComponentReference.Flexibilidad
      : contractID
        ? EnumPriceComponentReference.Contrato
        : undefined

  const [priceType, setPriceType] = useState<EnumPriceType | undefined>(
    props?.location?.state?.priceType
  )
  const [rounding, setRounding] = useState<EnumRedondeo | undefined>(undefined)

  const [showPriceCoefficients, setShowPriceCoefficients] =
    useState<boolean>(priceTypeIsApuntamiento)

  const [defaultPriceValueOption, setDefaultPriceValueOption] = useState<SelectOption[]>([
    {
      value: 1,
      label: t('priceComponent:forms.add.valueSection.defaultValueNotAvailable')
    }
  ])

  const showPricePeriod = priceTypeIsParametroPeriodo && editFormMode //&& !!parametroPeriodoPriceID

  const {
    isFetching: isFetchingPricePeriods,
    fetchData: pricePeriodData,
    getPricePeriodByID: refreshPricePeriod
  } = useGetPricePeriodByID(
    (errorMsg: string) => error(t(errorMsg)),
    parametroPeriodoPriceID ?? 0,
    showPricePeriod
  )

  const { flexibilities, isFetchingFlexibilities } = useFlexibilities({
    commodity: contract?.commodity,
    contractCode
  })

  const flexibilityData = useSelector(selectActiveFlexibility)

  const [priceComponentPriceID, setPriceComponentPriceID] = useState<number | undefined>(undefined)

  const { priceCoefficients, isFetchingPriceCoefficients } = usePriceCoefficients(
    { priceID: priceComponentPriceID },
    showPriceCoefficients
  )

  const [saveLoading, setSaveLoading] = useState<boolean>(false)
  const [priceCoefficientsList, setPriceCoefficientsList] = useState<
    PriceCoefficientTable[] | undefined | null
  >(coefficients)

  const [formState, setFormState] = useState<PriceComponentForm>({
    defaultValue: 1,
    contractID,
    contractCode,
    priceType,
    startDate:
      contract?.startDate || flexibilityData?.startDate
        ? moment(contract?.startDate ?? flexibilityData?.startDate).format('YYYY-MM-DD')
        : moment().startOf('year').format('YYYY-MM-DD'),
    flexibilityId: flexibilityID,
    toll: tollID, // required when editing a new price component type instance navigating from priceComponentTypeInstancesPage
    presionTension: presionTensionID, // required when editing a new price component type instance navigating from priceComponentTypeInstancesPage
    lead: leadID, // required when editing a new price component type instance navigating from priceComponentTypeInstancesPage
    regionImpuestos: regionImpuestosID, // required when editing a new price component type instance navigating from priceComponentTypeInstancesPage
    priceComponentTypeID // required when creating a new price component instance navigating from priceComponentTypeInstancesPage
  } as PriceComponentForm)

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  const [tollOptions, setTollOptions] = useState<SelectOption[]>([])
  const [tollLoading, setTollLoading] = useState<boolean>(false)
  const [presionTensionOptions, setPresionTensionOptions] = useState<SelectOption[]>([])
  const [presionTensionLoading, setPresionTensionLoading] = useState<boolean>(false)
  const [leadOptions, setLeadOptions] = useState<SelectOption[]>([])
  const [leadLoading, setLeadLoading] = useState<boolean>(false)
  const [regionImpuestosOptions, setRegionImpuestosOptions] = useState<SelectOption[]>([])

  const [pricePeriodFormDialogOpen, setPricePeriodFormDialogOpen] = useState<boolean>(false)
  const [pricePeriodFormMode, setPricePeriodFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)
  const [pricePeriodFormInputData, setPricePeriodFormInputData] = useState<
    PricePeriodValueGroupedModel | undefined
  >()

  const [isFetchingPriceComponentTypes, setIsFetchingPriceComponentTypes] = useState<boolean>(false)
  const [priceComponentTypes, setPriceComponentTypes] = useState<
    PriceComponentTypeModel[] | undefined
  >(undefined)
  const [priceComponentTypesOptions, setPriceComponentTypesOptions] = useState<SelectOption[]>([])

  const [isFetchingPriceValues, setIsFetchingPriceValues] = useState<boolean>(false)

  const getComponentTypeSelected = (priceComponentTypeID: number) => {
    let value: PriceComponentTypeModel | undefined = undefined
    if (priceComponentTypes && priceComponentTypes.length > 0) {
      value = priceComponentTypes.find(item => item.id === priceComponentTypeID)
    }
    return value
  }

  const onFormSaveHandlerV2 = async (formData: PriceComponentForm): Promise<any> => {
    if (!formData) {
      history.goBack()
      return
    }

    if (missingFieldsFormWithRequiredText(addFormSections, formData)) {
      error(t('priceComponent:forms.add.notifications.missingFields'))
      return
    }

    const priceComponentRequest = newPriceComponentRequestV2(formData)

    // add coefficients to request if they exist (Apuntamiento Case)
    if (priceCoefficientsList && priceCoefficientsList.length > 0) {
      priceComponentRequest.price.coefficients = newPriceCoefficientsRequest(priceCoefficientsList)
    }

    setSaveLoading(true)
    await createPriceComponentV2(priceComponentRequest)
      .then(response => {
        if (response.success) {
          info(t('priceComponent:forms.add.notifications.success'))
          history.goBack()
        } else {
          error(t('priceComponent:notifications.create.error'))
        }
      })
      .finally(() => setSaveLoading(false))
  }

  const onUpdateSaveHandler = async (formData: PriceComponentForm): Promise<any> => {
    if (!formData) {
      history.goBack()
      return
    }

    storeCoefficients(null)
    setSaveLoading(true)

    let priceCoefficients:
      | (PriceCoefficientAPI | CreatePriceComponentRequest['priceCoefficients'])[]
      | null = null

    if (priceCoefficientsList) {
      priceCoefficients = priceCoefficientsToAPIMapper(priceCoefficientsList)
    }

    priceCoefficients &&
      priceComponentPriceID &&
      (await updatePriceCoefficients({ idPrice: priceComponentPriceID, priceCoefficients })
        .then(response => {
          if (response.success) {
            info(t('priceComponent:forms.editValues.notifications.success'))
            // Puede venir de SettlementGroups o de Ficha de Cliente
            history.goBack()
          } else {
            showErrorDetailListDialog(
              t('priceComponent:notifications.rulebook'),
              response.error
                ? [{ code: response.error.code, message: `${response.error.message}` }]
                : [{ code: '412', message: '9001' }],
              t('priceComponent:notifications.update.error')
            )
          }
        })
        .finally(() => setSaveLoading(false)))

    if (priceType === EnumPriceType.Parametro && id) {
      await updatePriceComponentValue(id, formData?.value)
        .then(response => {
          if (response.success) {
            info(t('priceComponent:dataTable.notifications.updateValue.success'))
            // Puede venir de SettlementGroups o de Ficha de Cliente
            history.goBack()
          } else {
            showErrorDetailListDialog(
              t('priceComponent:notifications.rulebook'),
              response.error
                ? [{ code: response.error.code, message: `${response.error.message}` }]
                : [{ code: '412', message: '9000' }],
              t('priceComponent:notifications.update.error')
            )
          }
        })
        .finally(() => setSaveLoading(false))
    }
  }

  // ------------------- pricePeriod DataTable Action Handlers -----------------------------//

  const handleAddPricePeriod = () => {
    // Add Price Period Logic
    setPricePeriodFormMode(FormDialogMode.ADD)
    setPricePeriodFormDialogOpen(true)
  }

  const handleEditPricePeriod = (selectedRow: PricePeriodValueGroupedModel) => {
    // Edit Price Period Logic
    setPricePeriodFormMode(FormDialogMode.EDIT)
    setPricePeriodFormDialogOpen(true)
    setPricePeriodFormInputData(selectedRow)
  }

  const handleDeletePricePeriod = (selectedRow: PricePeriodValueGroupedModel) => {
    getConfirmation(ConfirmationDialogs.DELETE_PERIOD_PRICEVALUE).then(async () => {
      const priceValuesIds: number[] = Object.keys(selectedRow)
        .map(key => selectedRow[key]?.id)
        .filter(item => item !== undefined && item !== null)
      parametroPeriodoPriceID &&
        (await bulkDeletePricePeriodValues(parametroPeriodoPriceID, priceValuesIds).then(
          response => {
            if (response.success) {
              info(t('priceComponent:forms.pricePeriodValueForm.notifications.delete.success'))
              refreshPricePeriod(parametroPeriodoPriceID ?? 0)
            } else {
              error(t('priceComponent:forms.pricePeriodValueForm.notifications.delete.error'))
            }
          }
        ))
      closeConfirmation()
    })
  }

  // ------------------- pricePeriod Form Dialog Handlers -----------------------------//
  const handlePricePeriodFormDialogClose = async (formData: PricePeriodValueGroupedModel) => {
    if (formData) {
      const request = mapPricePeriodFormDataToRequest(formData)
      await updatePricePeriodValues(parametroPeriodoPriceID!, request).then(response => {
        if (response.success) {
          info(t('priceComponent:forms.pricePeriodValueForm.notifications.edit.success'))
          refreshPricePeriod(parametroPeriodoPriceID ?? 0)
        } else {
          error(t('priceComponent:forms.pricePeriodValueForm.notifications.edit.error'))
        }
      })
    }
    setPricePeriodFormDialogOpen(false)
  }

  // ------- Price Coefficients Card Handlers ------- //
  const handlePriceCoefficientsDataChange = (newState: PriceCoefficientTable[]) => {
    setPriceCoefficientsList(newState)
  }

  // Handlers Ends

  // Form Definition Starts

  const flexibilitiesOptions =
    flexibilityID && !contractCode
      ? [{ value: flexibilityID, label: flexibilityData?.description ?? '' }]
      : flexibilities
        ? flexibilityToSelectOptions(flexibilities)
        : []

  const addFormSections: Section[] = [
    {
      title: t('priceComponent:forms.add.mainSection.title'),
      inputs: [
        {
          name: 'contractCode',
          label: t('priceComponent:forms.add.mainSection.contractCode'),
          type: DataType.TEXT,
          readOnly: true,
          display: false
        },
        {
          name: 'flexibilityId',
          label: t('priceComponent:forms.add.mainSection.flexibility'),
          clearable: true,
          type: DataType.SELECT,
          isLoading: isFetchingFlexibilities,
          selectOptions: flexibilitiesOptions,
          defaultValue: flexibilityID,
          readOnly: !!flexibilityID,
          display: !!flexibilityID,
          onChange: flexID => {
            setFormState(prevState => ({
              ...prevState,
              flexibilityId: +flexID,
              flexibilityCode:
                flexibilitiesOptions.find(option => option.value === +flexID)?.label ?? ''
            }))
          }
        },
        {
          name: 'priceComponentTypeID',
          label: t('priceComponent:forms.add.mainSection.priceComponentType'),
          selectOptions: priceComponentTypesOptions,
          helpText:
            isFetchingPriceCoefficients ||
            isFetchingPriceComponentTypes ||
            priceComponentTypesOptions.length > 0
              ? ''
              : t('priceComponent:forms.add.mainSection.componentHelpText'),
          isLoading: isFetchingPriceComponentTypes,
          type: DataType.SELECT,
          required: true,
          display: true,
          inputSize: 8,
          readOnly: !!id || editFormMode || addInstance,
          onChange: async priceComponentTypeId => {
            const priceComponentType =
              getComponentTypeSelected(+priceComponentTypeId) ?? ({} as PriceComponentTypeModel)
            if (priceComponentType) {
              setFormState(prevState => ({
                ...prevState,

                priceComponentTypeID: +priceComponentTypeId,
                priceComponentTypeCode: priceComponentType?.code,
                round: priceComponentType?.priceDummy?.rounding!,
                priceUnit: priceComponentType?.priceDummy?.unitPrice!
              }))
              setRounding(priceComponentType.priceDummy?.rounding!)

              const priceComponentDefault = await getPriceComponentDefaults(
                referenceType,
                priceComponentType.priceType,
                commodity,
                +priceComponentTypeId
              )
              if (
                priceComponentDefault.success &&
                priceComponentDefault.data &&
                priceComponentDefault.data?.length > 0
              ) {
                const { defaultValue, currentValue } = await getPriceCurrentValuesByIDs(
                  priceComponentDefault.data[0].idPrice ?? 0,
                  null
                )
                if (defaultValue !== null && defaultValue !== undefined) {
                  setDefaultPriceValueOption([
                    {
                      value: 1,
                      label: (defaultValue as number)
                        .toFixed(priceComponentType.priceDummy?.rounding ?? 6)
                        .replace('.', ',')
                    }
                  ])
                }
              }
            }
          }
        }
      ]
    },
    {
      title: t('priceComponent:forms.add.componentSection.title'),
      inputs: [
        {
          name: 'priceUnit',
          label: t('priceComponent:forms.add.componentSection.priceUnit'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
          isLoading: isFetchingPriceComponentTypes,
          readOnly: true
        },
        {
          name: 'round',
          label: t('priceComponent:forms.add.componentSection.round'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
          isLoading: isFetchingPriceComponentTypes,
          readOnly: true
        }
      ]
    }
  ]

  if (priceTypeIsParametro || priceTypeIsParametroPeriodo || priceTypeIsApuntamiento) {
    addFormSections.push({
      title: t('priceComponent:forms.add.valueSection.title'),
      inputs: [
        {
          name: 'defaultValue',
          label: t('priceComponent:forms.add.valueSection.defaultValue'),
          type: DataType.SELECT,
          selectOptions: defaultPriceValueOption,
          isLoading: isFetchingPriceValues,
          readOnly: true
        },
        {
          name: 'value',
          label: t('priceComponent:forms.add.valueSection.value'),
          type: DataType.NUMBER,
          isLoading: addInstance ? false : isFetchingPriceValues,
          customDecimalScale: rounding ?? 6,
          display: priceType === EnumPriceType.Parametro,
          required: true
        },
        {
          name: 'toll',
          label: t('priceComponent:forms.add.mainSection.toll'),
          type: DataType.SELECT,
          selectOptions: tollOptions,
          isLoading: tollLoading,
          display: priceComponentTypeReference === EnumPriceComponentReference.Peaje,
          required: priceComponentTypeReference === EnumPriceComponentReference.Peaje,
          readOnly: !!tollID,
          inputSize: 6
        },
        {
          name: 'presionTension',
          label: t('priceComponent:forms.add.mainSection.presionTension'),
          type: DataType.SELECT,
          selectOptions: presionTensionOptions,
          isLoading: presionTensionLoading,
          display: priceComponentTypeReference === EnumPriceComponentReference.PresionTension,
          required: priceComponentTypeReference === EnumPriceComponentReference.PresionTension,
          readOnly: !!presionTensionID,
          inputSize: 6
        },
        {
          name: 'lead',
          label: t('priceComponent:forms.add.mainSection.lead'),
          type: DataType.SELECT,
          selectOptions: leadOptions,
          isLoading: leadLoading,
          display: priceComponentTypeReference === EnumPriceComponentReference.Lead,
          required: priceComponentTypeReference === EnumPriceComponentReference.Lead,
          readOnly: !!leadID,
          inputSize: 6,
          onChange: leadId => {
            setFormState(prevState => ({
              ...prevState,
              lead: +leadId,
              leadCode: leadOptions.find(option => option.value === +leadId)?.label ?? ''
            }))
          }
        },
        {
          name: 'regionImpuestos',
          label: t('priceComponent:forms.add.mainSection.regionImpuestos'),
          type: DataType.SELECT,
          selectOptions: regionImpuestosOptions,
          isLoading: false,
          display: priceComponentTypeReference === EnumPriceComponentReference.RegionImpuestos,
          required: priceComponentTypeReference === EnumPriceComponentReference.RegionImpuestos,
          readOnly: !!regionImpuestosID,
          inputSize: 6
        }
      ]
    })
  }

  if (priceTypeIsParametroPeriodo && addFormMode) {
    addFormSections.push({
      title: t('priceComponent:forms.add.periodValueSection.title'),
      inputs: [
        {
          name: 'startDate',
          label: t('priceComponent:forms.add.valueSection.startDate'),
          type: DataType.DATE,
          defaultValue: moment().startOf('year').toDate(),
          required: true
        },
        {
          name: 'endDate',
          label: t('priceComponent:forms.add.valueSection.endDate'),
          type: DataType.DATE,
          display: false
        },
        {
          name: 'period1Value',
          label: t('priceComponent:forms.add.valueSection.period1'),
          type: DataType.NUMBER,
          required: true,
          customDecimalScale: rounding ?? 6,
          clearable: true
        },
        {
          name: 'period2Value',
          label: t('priceComponent:forms.add.valueSection.period2'),
          type: DataType.NUMBER,
          customDecimalScale: rounding ?? 6,
          clearable: true
        },
        {
          name: 'period3Value',
          label: t('priceComponent:forms.add.valueSection.period3'),
          type: DataType.NUMBER,
          customDecimalScale: rounding ?? 6,
          clearable: true
        },
        {
          name: 'period4Value',
          label: t('priceComponent:forms.add.valueSection.period4'),
          type: DataType.NUMBER,
          customDecimalScale: rounding ?? 6,
          clearable: true
        },
        {
          name: 'period5Value',
          label: t('priceComponent:forms.add.valueSection.period5'),
          type: DataType.NUMBER,
          customDecimalScale: rounding ?? 6,
          clearable: true
        },
        {
          name: 'period6Value',
          label: t('priceComponent:forms.add.valueSection.period6'),
          type: DataType.NUMBER,
          customDecimalScale: rounding ?? 6,
          clearable: true
        }
      ]
    })
  }

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponent:forms.add.analytics')
    },
    saveDisabled:
      saveLoading ||
      // isFetchingPriceComponentsDefault ||
      // isFetchingPriceComponent ||
      isFetchingPriceCoefficients ||
      isFetchingFlexibilities ||
      // isFetchingPrice ||
      (parametroPeriodoPriceID && editFormMode)
        ? true
        : undefined,
    ...(!priceTypeIsParametroPeriodo || addFormMode
      ? {
          onSave: data => (editFormMode ? onUpdateSaveHandler(data) : onFormSaveHandlerV2(data))
        }
      : {})
  }

  // Form Config Ends

  // Hooks Starts

  useEffect(() => {
    const fetchPriceComponentTypes = async () => {
      setIsFetchingPriceComponentTypes(true)
      await getPriceComponentTypes(referenceType, priceType, commodity, true)
        .then(response => {
          if (response.success && response.data) {
            setPriceComponentTypes(response.data)
            setPriceComponentTypesOptions(
              response.data
                .filter(pct => !componentTypeToExclude?.includes(pct.id!))
                .map(pct => ({
                  value: pct.id!,
                  label: pct.description ? `${pct.code} - ${pct.description}` : pct.code
                }))
            )
          } else {
            error(t('priceComponent:forms.add.notifications.priceComponentTypeError'))
          }
        })
        .finally(() => setIsFetchingPriceComponentTypes(false))
    }

    const fetchPriceComponentTypeById = async (
      priceComponentTypeID: number,
      priceComponentID?: number
    ) => {
      setIsFetchingPriceComponentTypes(true)
      await Promise.all([
        addInstance
          ? Promise.resolve({ success: true, data: null }) // this is to handle null case when adding a new instance of a priceComponentType
          : getPriceComponent({ id: priceComponentID }),
        getPriceComponentTypeById(priceComponentTypeID),
        getPriceComponentDefaults(referenceType, priceType, commodity, priceComponentTypeID)
      ])
        .then(async ([priceComponentResponse, priceComponentTypeResponse, defaultsResponse]) => {
          if (
            !priceComponentResponse.success ||
            !priceComponentTypeResponse.success ||
            !defaultsResponse.success
          ) {
            error(t('priceComponent:forms.add.notifications.priceComponentTypeError'))
            return
          }

          let defaultIdPrice: number | null = null
          let priceComponentIdPrice: number | null = null
          let newFormState: PriceComponentForm = { ...formState }

          if (priceComponentResponse.data) {
            let priceComponent = priceComponentResponse.data
            priceComponentIdPrice = priceComponent.idPrice

            if (priceComponent.regionImpuestos) {
              const regionImpuestosEnum =
                EnumRegionImpuestos[
                  priceComponent.regionImpuestos as keyof typeof EnumRegionImpuestos
                ]
              if (regionImpuestosEnum !== undefined) {
                newFormState.regionImpuestos = regionImpuestosEnum
              }
            }
          }

          if (priceComponentTypeResponse.data) {
            const priceComponentType = priceComponentTypeResponse.data
            setPriceComponentTypesOptions([
              {
                value: priceComponentType.id!,
                label: priceComponentType.description
                  ? `${priceComponentType.code} - ${priceComponentType.description}`
                  : priceComponentType.code
              }
            ])
            newFormState.priceComponentTypeID = priceComponentType.id!
            newFormState.priceComponentTypeCode = priceComponentType.code
            newFormState.round = priceComponentType.priceDummy?.rounding!
            newFormState.priceUnit = priceComponentType.priceDummy?.unitPrice!
            setRounding(priceComponentType.priceDummy?.rounding!)
          }

          if (defaultsResponse.data && defaultsResponse.data.length > 0) {
            defaultIdPrice = defaultsResponse.data[0]?.idPrice ?? null
          }

          const { defaultValue, currentValue } = await getPriceCurrentValuesByIDs(
            defaultIdPrice,
            priceType === EnumPriceType.Parametro ? priceComponentIdPrice : null // there is no need to call priceCurrentValue priceComponentIdPrice. apuntamiento o parametroPeriodo has periodValues(parametroPeriodo) or coefficients(apuntamiento) lists
          )

          if (defaultValue !== null && defaultValue !== undefined) {
            setDefaultPriceValueOption([
              {
                value: 1,
                label: (defaultValue as number).toFixed(newFormState.round ?? 6).replace('.', ',')
              }
            ])
          }

          newFormState.value = currentValue
          setPriceComponentPriceID(priceComponentIdPrice ?? undefined)
          setFormState(newFormState)

          if (currentValue === null && priceType === EnumPriceType.Parametro && !addInstance) {
            getConfirmation(ConfirmationDialogs.NULL_PRICE_VALUE).then(async () => {
              history.push(NonAuthRoutes.pricingEdit, {
                priceId: priceComponentIdPrice,
                commodity: commodity,
                priceType: priceType,
                priceComponentTypeId: formState.priceComponentTypeID
              })
              closeConfirmation()
            })
          }
        })
        .finally(() => setIsFetchingPriceComponentTypes(false))
    }

    if ((id && priceComponentTypeID) || (addInstance && priceComponentTypeID)) {
      // EDIT Form use Case (id and priceComponentTypeID != null) or ADD Instance from PriceComponentTypeInstancesPage use Case (priceComponentTypeID != null && id=null)
      fetchPriceComponentTypeById(priceComponentTypeID, id)
    } else {
      // ADD Form use Case
      fetchPriceComponentTypes()
    }
  }, [id, priceComponentTypeID])

  const getPriceCurrentValuesByIDs = async (
    defaultPriceID: number | null,
    priceID: number | undefined | null
  ): Promise<{
    defaultValue: number | null
    currentValue: number | null
  }> => {
    const pricesIds: number[] = []
    let defaultValue: number | null = null
    let currentValue: number | null = null

    defaultPriceID && pricesIds.push(defaultPriceID)
    priceID && pricesIds.push(priceID)

    if (pricesIds.length) {
      setIsFetchingPriceValues(true)

      const today = moment().format('YYYY-MM-DD')

      const priceCurrentValuesResponse = await fetchPriceCurrentValues(
        pricesIds,
        today,
        today
      ).finally(() => {
        setIsFetchingPriceValues(false)
      })

      if (priceCurrentValuesResponse.success && priceCurrentValuesResponse.data) {
        const priceCurrentValues = priceCurrentValuesResponse.data

        defaultPriceID &&
          (defaultValue =
            getPriceValueFromPriceValuesArray(defaultPriceID, priceCurrentValues)?.value ?? null)
        priceID &&
          (currentValue =
            getPriceValueFromPriceValuesArray(priceID, priceCurrentValues)?.value ?? null)
      }
      // if (priceID) {
      //   const priceValue = getPriceValueFromPriceValuesArray(priceID, priceCurrentValues)
      //   if (priceValue?.value === null || priceValue?.value === undefined) {
      //     setPriceValueIsNull(true)
      //   } else {
      //     setPriceValueIsNull(false)
      //   }
      //   setPrice(prevState => {
      //     if (!prevState) return prevState
      //     return {
      //       ...prevState,
      //       value: getPriceValueFromPriceValuesArray(priceID, priceCurrentValues)?.value ?? null
      //     }
      //   })
      // }
    }

    return { defaultValue, currentValue }
  }

  const getPriceValueFromPriceValuesArray = (priceID: number, priceValues: PriceValuesModel[]) => {
    const priceValue = priceValues.find(pv => pv.priceId === priceID)
    return priceValue ? priceValue : null
  }

  // useEffect(() => {
  // price && mapPrice(price)
  // price && setRounding(price.round)

  //   if (editFormMode && priceValueIsNull) {
  //     getConfirmation(ConfirmationDialogs.NULL_PRICE_VALUE).then(async () => {
  //       history.push(NonAuthRoutes.pricingEdit, {
  //         priceId: price?.id,
  //         commodity: commodity,
  //         priceType: priceType,
  //         priceComponentTypeId: formState.priceComponentTypeID
  //       })
  //       closeConfirmation()
  //     })
  //   }
  // }, [price])

  // useEffect(() => {
  //   if (priceComponent) {
  //     setFormState(prevState => ({
  //       ...prevState,
  //       ...(priceComponentAPItoFormMapper(priceComponent) as PriceComponentForm)
  //     }))
  //     setPriceType(priceComponent.priceType)
  //   }
  // }, [priceComponent])

  useEffect(() => {
    if (priceCoefficients && priceCoefficientsList == null) {
      setPriceCoefficientsList(priceCoefficientAPIListtoTableMapper(priceCoefficients))
    }
  }, [priceCoefficients])

  useEffect(() => {
    const getTollSelectOptions = async () => {
      setTollLoading(true)
      await fetchTolls(commodity as EnumCommodity)
        .then(response => {
          if (response.success && response.data) {
            setTollOptions(
              response.data
                .filter(t => !instancesToFilter.includes(t.id))
                .map(t => ({ value: t.id, label: t.description }))
            )
          } else {
            error(t('priceComponent:forms.add.notifications.tollError'))
          }
        })
        .finally(() => setTollLoading(false))
    }

    if (priceComponentTypeReference === EnumPriceComponentReference.Peaje && commodity) {
      getTollSelectOptions()
    }
  }, [priceComponentTypeReference, tollID, commodity])

  useEffect(() => {
    const getPresionTensionSelectOptions = async () => {
      setPresionTensionLoading(true)
      await fetchPressureGroup(commodity as EnumCommodity)
        .then(response => {
          if (response.success && response.data) {
            setPresionTensionOptions(
              response.data.filter(p => !instancesToFilter.includes(p.value))
            )
          } else {
            error(t('priceComponent:forms.add.notifications.presionTensionError'))
          }
        })
        .finally(() => setPresionTensionLoading(false))
    }

    if (priceComponentTypeReference === EnumPriceComponentReference.PresionTension && commodity) {
      getPresionTensionSelectOptions()
    }
  }, [priceComponentTypeReference, presionTensionID, commodity])

  useEffect(() => {
    const getLeadSelectOptions = async () => {
      setLeadLoading(true)
      await getLeads(commodity as EnumCommodity)
        .then(response => {
          if (response.success && response.data) {
            setLeadOptions(
              response.data
                .filter(l => !instancesToFilter.includes(l.id))
                .map(l => ({ value: l.id, label: l.code }))
            )
            //#79538 - Cambiamos en el backend para que se puedan crear components con referencia al leadId y tambien
            //con leadCode pero se prioriza el leadCode si viene informado. Se nos pide cambiar esto para
            //que siempre utilicemos el leadId para evitar ambiguedades en caso de que existan Leads con el mismo código.
            //  Por eso ya no seteamos el leadCode en el formState al cargar las opciones de Lead, sino que lo hacemos directamente en el onChange del select de Lead, y se setea a partir del label de la opción seleccionada.

            // if (leadID) {
            //   const leadCode = response.data.find(l => l.id === leadID)?.code
            //   setFormState(prevState => ({ ...prevState, leadCode })) // this is important to handle createPriceComponentV2 Mapping
            // }
          } else {
            error(t('priceComponent:forms.add.notifications.leadError'))
          }
        })
        .finally(() => setLeadLoading(false))
    }

    if (priceComponentTypeReference === EnumPriceComponentReference.Lead && commodity) {
      getLeadSelectOptions()
    }
  }, [priceComponentTypeReference, leadID, commodity])

  useEffect(() => {
    if (priceComponentTypeReference === EnumPriceComponentReference.RegionImpuestos) {
      const options = enumToSelectOptionArray(EnumRegionImpuestos, 'enums:EnumRegionImpuestos')
      setRegionImpuestosOptions(options)
    }
  }, [priceComponentTypeReference])

  // Hooks Ends

  return (
    <Page
      layout={PageLayout.SINGLE_COLUMN}
      fullWidth={false}
      notification={globalNotification}
      error={errorProps}
    >
      <PageHeader title={t(`priceComponent:forms.${editFormMode ? 'editValues' : 'add'}.title`)}>
        {priceTypeIsParametroPeriodo && editFormMode && (
          <Button
            onClick={() => {
              history.goBack()
            }}
          >
            {t('priceComponent:header.backButton')}
          </Button>
        )}
      </PageHeader>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card raised>
            <Form
              sections={addFormSections}
              data={id && isFetchingPriceComponentTypes ? undefined : formState}
              options={options}
            />
          </Card>
        </Grid>
        {priceType === EnumPriceType.Apuntamiento && (
          <Grid item xs={12}>
            <PriceCoefficientsCard
              raised
              priceCoefficientsList={priceCoefficientsList}
              isLoading={isFetchingPriceComponentTypes || isFetchingPriceCoefficients}
              rounding={rounding ?? 6}
              onTableDataChange={handlePriceCoefficientsDataChange}
              style={{ margin: '16px 0 0 0', boxShadow: '1' }}
            />
          </Grid>
        )}
        {priceTypeIsParametroPeriodo && (
          <Grid item xs={12}>
            <PricePeriodCard
              raised
              show={showPricePeriod}
              isFetchingPricePeriods={isFetchingPriceComponentTypes || isFetchingPricePeriods}
              pricePeriodData={pricePeriodData}
              rounding={rounding ?? 6}
              onAddPricePeriod={handleAddPricePeriod}
              onEditPricePeriod={handleEditPricePeriod}
              onDeletePricePeriod={handleDeletePricePeriod}
            />
          </Grid>
        )}
      </Grid>
      <ErrorDetailList {...errorDetailListDialogProps} />

      <PricePeriodFormDialog
        rounding={rounding ?? 6}
        formMode={pricePeriodFormMode}
        open={pricePeriodFormDialogOpen}
        onClose={handlePricePeriodFormDialogClose}
        inputData={pricePeriodFormInputData}
      />
    </Page>
  )
}
